import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCardIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const Payments = () => {
  const [cashEnabled, setCashEnabled] = useState(true);
  const [cardEnabled, setCardEnabled] = useState(true);
  const [depositRequired, setDepositRequired] = useState(false);
  const [depositPercentage, setDepositPercentage] = useState('');
  const [surchargePercentage, setSurchargePercentage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const { user, isAdminOrManager } = useAuth();

  // Load current payment settings on component mount
  useEffect(() => {
    if (isAdminOrManager) {
      fetchPaymentSettings();
    }
  }, [isAdminOrManager]);

  const fetchPaymentSettings = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      const response = await axios.get('/api/settings/financial/payment', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data && response.data.data) {
        const paymentData = response.data.data;
        setCashEnabled(paymentData.cash?.enabled ?? true);
        setCardEnabled(paymentData.card?.enabled ?? true);
        setDepositRequired(paymentData.deposit?.required ?? false);
        setDepositPercentage(paymentData.deposit?.percentage ? (paymentData.deposit.percentage * 100).toFixed(2) : '');
        setSurchargePercentage(paymentData.surcharge?.percentage ? (paymentData.surcharge.percentage * 100).toFixed(2) : '');
      }
    } catch (error) {
      console.error('Error fetching payment settings:', error);
      if (error.response?.status === 403) {
        setError('You do not have permission to view payment settings');
      } else if (error.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/login'), 2000);
      } else if (error.response?.status !== 404) {
        setError('Failed to load payment settings');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePercentageChange = (value, setter) => {
    // Allow empty string for clearing
    if (value === '') {
      setter('');
      return;
    }

    // Validate numerical input with up to 2 decimal places
    const regex = /^\d{0,2}(\.\d{0,2})?$/;
    if (regex.test(value) && parseFloat(value) <= 100) {
      setter(value);
    }
  };

  const handleSave = async () => {
    // Validate deposit percentage if deposit is required
    if (depositRequired && (!depositPercentage || depositPercentage === '')) {
      setError('Please enter a deposit percentage when deposit is required');
      return;
    }

    let numericDepositPercentage = 0;
    if (depositRequired) {
      numericDepositPercentage = parseFloat(depositPercentage);
      if (isNaN(numericDepositPercentage) || numericDepositPercentage < 0 || numericDepositPercentage > 100) {
        setError('Deposit percentage must be between 0 and 100');
        return;
      }
    }

    // Validate surcharge percentage
    let numericSurchargePercentage = 0;
    if (surchargePercentage && surchargePercentage !== '') {
      numericSurchargePercentage = parseFloat(surchargePercentage);
      if (isNaN(numericSurchargePercentage) || numericSurchargePercentage < 0 || numericSurchargePercentage > 100) {
        setError('Surcharge percentage must be between 0 and 100');
        return;
      }
    }

    try {
      setIsLoading(true);
      setError('');
      setSuccessMessage('');

      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      await axios.put('/api/settings/financial/payment', {
        cash: { enabled: cashEnabled },
        card: { enabled: cardEnabled },
        deposit: {
          required: depositRequired,
          percentage: numericDepositPercentage / 100 // Convert to decimal
        },
        surcharge: {
          percentage: numericSurchargePercentage / 100 // Convert to decimal
        }
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setSuccessMessage('Payment settings saved successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving payment settings:', error);
      setError('Failed to save payment settings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    fetchPaymentSettings(); // Reset to original values
    setError('');
    setSuccessMessage('');
  };

  // Show loading state while checking permissions
  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  // Show error if user doesn't have permission
  if (!isAdminOrManager) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600">You do not have permission to access payment settings.</div>
      </div>
    );
  }

  return (
    <div>
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Payment Settings
          </h2>
          <p className="mt-1 text-gray-600">
            Configure payment methods and processing options
          </p>
        </div>
      </div>

      <div className="mt-8">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center mb-6">
            <CreditCardIcon className="h-6 w-6 text-gray-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Payment Configuration</h3>
          </div>

          <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
            {/* Credit Card Processing - Surcharge Only */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">Credit Card Processing</h4>

              {/* Surcharge Percentage */}
              <div>
                <label htmlFor="surchargePercentage" className="block text-sm font-medium text-gray-700 mb-2">
                  Surcharge %
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="surchargePercentage"
                    value={surchargePercentage}
                    onChange={(e) => handlePercentageChange(e.target.value, setSurchargePercentage)}
                    placeholder="Enter surcharge percentage (e.g., 3.00)"
                    className="block w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 pr-8"
                    disabled={isLoading}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 text-sm">%</span>
                  </div>
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  Additional percentage to charge for credit card payments (0-100%)
                </p>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {successMessage && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-600">{successMessage}</p>
              </div>
            )}

            <div className="flex space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Payments;